import React, { useContext, useEffect } from "react";
import ClientExercisesSearch from "./ClientExercisesSearch";
import { UserExerciseDto } from "@growth-machine-llc/stridist-api-client";
import ClientExerciseHistoryContainer from "./ClientExerciseHistoryContainer";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import MinimizedDrawerContext from "../../contexts/MinimizedDrawerContext";
import { JourneyRunContext } from "../../contexts/JourneyRunContext";
import HistoryExerciseListModalContext from "../../contexts/HistoryExerciseListModalContext";
import { BaseDialog } from "../dialog/BaseDialog";
import makeStyles from "@mui/styles/makeStyles";
import {
  ClientExercisesProvider,
  useClientExercises,
} from "./ClientExercisesContext";
import { EditorProgramContext } from "../new-editor/hooks";
import { ExerciseLibraryDrawer } from "../exercise-library/ExerciseLibraryDrawer";
import { DefaultLoader } from "../loading/DefaultLoader";
import { InfoBox } from "../info/InfoBox";
import { ReactComponent as TrainingIcon } from "../../icons/personal-training.svg";
import { useNavigate } from "react-router-dom";
import { COACH_CLIENT_OVERVIEW_ROUTE, HOME_ROUTE } from "../../routes/routes";
import { useUserIsClient } from "../../hooks/useCurrentUser";

const useStyles = makeStyles((theme) => ({
  dialogHeader: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(2, 2, 0, 2),
    margin: 0,
  },
  dialogTitle: {
    fontSize: 16,
  },
  dialogContainer: {
    padding: 0,
  },
  dialogPaper: {
    borderRadius: 12,
    overflow: "hidden",
  },
  dialogSearchBox: {
    paddingTop: 0,
    borderTop: "none",
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: theme.spacing(1),
    textAlign: "center",
  },
  emptyTitle: {
    marginTop: theme.spacing(2),
  },
  emptySubtitle: {
    whiteSpace: "pre-line",
  },
}));

interface IClientExerciseHistoryPageProps {
  clientId: number;
  clientUsername?: string;
}

interface ClientExerciseHistoryPageContentProps
  extends IClientExerciseHistoryPageProps {}

const ClientExerciseHistoryPageContent = (
  props: ClientExerciseHistoryPageContentProps,
) => {
  const { clientId, clientUsername } = props;
  const s = useStyles();
  const theme = useTheme();
  const downLg = useMediaQuery(theme.breakpoints.down("lg"));

  const isClient = useUserIsClient();

  const [exerciseAssetDrawerOpen, setExerciseAssetDrawerOpen] =
    React.useState(false);

  const {
    exercises,
    selectedExercise,
    setSelectedExercise,
    isFetching,
    isLoadingInitial,
  } = useClientExercises();
  const { exerciseListModalOpen, setExerciseListModalOpen } = useContext(
    HistoryExerciseListModalContext,
  );

  const navigate = useNavigate();

  const { setMinimizedDrawer } = useContext(MinimizedDrawerContext);
  const { isRunning } = useContext(JourneyRunContext);

  useEffect(() => {
    if (!isRunning && selectedExercise) {
      setMinimizedDrawer(true);
    }
  }, [isRunning, setMinimizedDrawer, selectedExercise]);

  useEffect(() => {
    if (!downLg) {
      setExerciseListModalOpen(false);
    }
  }, [downLg, setExerciseListModalOpen]);

  const handleExerciseClick = (exercise: UserExerciseDto) => {
    setSelectedExercise(exercise);
    if (downLg) {
      setExerciseListModalOpen(false);
    }
  };

  const handleCloseExerciseDrawer = () => {
    setExerciseAssetDrawerOpen(false);
  };

  const handleOpenExerciseDrawer = () => {
    setExerciseAssetDrawerOpen(true);
  };

  const link = React.useMemo(
    () =>
      isClient
        ? HOME_ROUTE
        : COACH_CLIENT_OVERVIEW_ROUTE.replace(":username", clientUsername),
    [isClient, clientUsername],
  );

  const handleClick = React.useCallback(() => {
    navigate(link);
  }, [link]);

  const displayEmptyState =
    !selectedExercise && !isFetching && exercises.length === 0;

  if (isLoadingInitial && exercises.length === 0 && !selectedExercise) {
    return <DefaultLoader fillScreen />;
  }

  return (
    <>
      <Box
        display={"flex"}
        flexDirection={"row"}
        gap={2}
        marginRight={2.5}
        justifyContent={displayEmptyState ? "center" : "flex-start"}
        marginLeft={2.5}
        // use vh to center the content vertically when there is no selected exercise and the list is empty for the small screen
        height={displayEmptyState ? "calc(80vh)" : "100%"}
      >
        {!downLg && !displayEmptyState && (
          <ClientExercisesSearch
            onExerciseSelect={handleExerciseClick}
            clientId={clientId}
            selectedExerciseId={selectedExercise?.id}
          />
        )}
        {displayEmptyState && (
          <InfoBox
            image={<TrainingIcon />}
            title="No exercises completed"
            subtitle={
              isClient
                ? `Don't worry, you just need to complete the exercise to start your statistics.\n Check your home page, maybe a workout is already waiting for you.`
                : `Exercise history will appear here once your client completes their first workout.`
            }
            classes={{
              title: s.emptyTitle,
              subtitle: s.emptySubtitle,
            }}
            className={s.emptyState}
          >
            <Button variant="contained" fullWidth onClick={handleClick}>
              {isClient ? "Go to Home" : "Go to Client Overview"}
            </Button>
          </InfoBox>
        )}
        {selectedExercise && (
          <ClientExerciseHistoryContainer
            key={selectedExercise.id}
            userExercise={selectedExercise}
            onExerciseSelect={handleExerciseClick}
            onOpenExerciseDrawer={handleOpenExerciseDrawer}
            clientId={clientId}
          />
        )}
      </Box>
      <BaseDialog
        open={exerciseListModalOpen}
        classes={{
          header: s.dialogHeader,
          container: s.dialogContainer,
          paper: s.dialogPaper,
        }}
        showClose={false}
      >
        <ClientExercisesSearch
          onExerciseSelect={handleExerciseClick}
          clientId={clientId}
          selectedExerciseId={selectedExercise?.id}
          modalView={true}
          onModalClose={() => setExerciseListModalOpen(false)}
        />
      </BaseDialog>
      <EditorProgramContext.Provider value={{ programId: 0 }}>
        {exerciseAssetDrawerOpen && (
          <ExerciseLibraryDrawer
            exerciseAsset={selectedExercise.exercise}
            onUpdate={handleCloseExerciseDrawer}
            onCancel={handleCloseExerciseDrawer}
            onClose={handleCloseExerciseDrawer}
            onRemove={handleCloseExerciseDrawer}
            open={exerciseAssetDrawerOpen}
            disableChangeExercise={true}
          />
        )}
      </EditorProgramContext.Provider>
    </>
  );
};

const ClientExerciseHistoryPage = (props: IClientExerciseHistoryPageProps) => {
  const { clientId, clientUsername } = props;

  return (
    <ClientExercisesProvider clientId={clientId}>
      <ClientExerciseHistoryPageContent
        clientId={clientId}
        clientUsername={clientUsername}
      />
    </ClientExercisesProvider>
  );
};

export default ClientExerciseHistoryPage;
